import React, { useCallback, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import useSWR from 'swr'
import { Badge, Banner, ImageSlot } from '../../../components'
import MatchSelect, { MATCH_SEARCH_HINT } from '../../MatchSelect'
import LinkRefSelector, {
  EMPTY_LINK_REF, EXTERNAL_PAGE_TYPE, LinkRefValue, isExternalLinkRef, validateLinkRef,
} from '../LinkRefSelector'
import { useWidgetSite } from '../../../contexts/WidgetSiteContext'
import { useDragReorder } from '../../../hooks'
import {
  PageTypeInfo, fetchArtworkTemplates, fetchMatchImages, fetchPageTypeConfig, uploadHeroImage,
} from '../../../services/api'
import { arrayMove } from '../../../utils/arrayMove'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import styles from './itemListEditor.module.scss'

// Stored metadata shape — mirrors what ACE's hydrateHeroBanner parses
// (apps/ace/internal/service/layout_db.go). A slide is authored in one of two
// modes:
//
//   Image slide — `image_url` (desktop, the required default) plus optional
//   image_url_tablet/mobile/tv device variants (ACE falls back to desktop) and
//   a required destination: `link_ref` naming a page, or `link` holding a raw
//   href. Plain CDN-URL strings, NOT the tc_image_link flow banner_image uses.
//   content_type/content_id may additionally link the slide to a live match
//   for the derived badge (legacy linkage, no UI).
//
//   Match-card slide — content_type "match" + content_id plus the two hero
//   artwork-template keys (hero_template_desktop / hero_template_mobile). No
//   authored image or link: the render pipeline generates the imagery per
//   lifecycle phase (UPCOMING/LIVE/REPLAY badge baked in) and ACE derives the
//   click-through from the content ref.
//
// size defaults to LARGE when absent. There is no authored badge: ACE derives
// HeroSlide.badge (or the template bakes it), so the CMS doesn't offer one.
export interface HeroSlideMetadata {
  id: string
  image_url: string
  image_url_tablet?: string
  image_url_mobile?: string
  image_url_tv?: string
  /** Image description for screen readers. Sent to the upload endpoint as the
   * tc_image alt text; persisted here so it survives reloads and edits, and
   * served to clients as HeroSlide.altText. */
  alt_text?: string
  /** Raw href — an off-site destination, and every slide authored before the
   * LinkRef picker landed. ACE serves it as-authored. */
  link: string
  /** The click-through as a page reference instead of a raw href: ACE resolves
   * it per request, so a renamed slug self-heals. Mutually exclusive with a
   * non-empty `link` — the picker writes one or the other. */
  link_ref?: HeroSlideLinkRef
  content_type?: string
  content_id?: string
  /** tc_artwork_template keys (template_type 'hero') for generated match-card
   * slides — one per rendered surface (tablet/TV serve the desktop render). */
  hero_template_desktop?: string
  hero_template_mobile?: string
}

/** Match-card mode: authored as a match ref, not an image. Legacy slides with
 * an authored image AND a content link stay image slides. */
const isMatchSlide = (s: HeroSlideMetadata): boolean =>
  s.content_type === 'match' && !s.image_url

/** A slide's stored destination → the picker's value. A page reference is the
 * authored form; a raw `link` loads as an external URL, which is what it is —
 * the picker's external mode accepts both absolute URLs and on-site paths, so
 * slides authored before the picker existed round-trip unchanged. */
const slideLinkRefValue = (slide: HeroSlideMetadata): LinkRefValue => {
  if (slide.link_ref?.page_type) {
    return {
      page_type: slide.link_ref.page_type,
      page_id: slide.link_ref.page_id ?? '',
      node_id: slide.link_ref.node_id ?? '',
      external_url: '',
    }
  }
  if (slide.link) {
    return { ...EMPTY_LINK_REF, page_type: EXTERNAL_PAGE_TYPE, external_url: slide.link }
  }
  return EMPTY_LINK_REF
}

/** The picker's value → the slide's stored fields. The two destination fields
 * are mutually exclusive, so each mode clears the other: an external link is
 * the raw href in `link`, a page link is the reference in `link_ref`. */
const slideLinkPatch = (v: LinkRefValue): Partial<HeroSlideMetadata> => {
  if (isExternalLinkRef(v)) {
    return { link: v.external_url, link_ref: undefined }
  }
  if (!v.page_type) return { link: '', link_ref: undefined }
  return {
    link: '',
    link_ref: {
      page_type: v.page_type,
      page_id: v.page_id || undefined,
      node_id: v.node_id || undefined,
    },
  }
}

/** The collapsed row's destination line: the href for a raw link, the page's
 * name for a reference — its subject would cost a lookup per slide, and the
 * expanded picker already shows it. */
const slideLinkSummary = (slide: HeroSlideMetadata, types: PageTypeInfo[]): string => {
  if (slide.link) return slide.link
  const code = slide.link_ref?.page_type
  if (!code) return ''
  return types.find(t => t.code === code)?.title ?? code
}

// The four device image slots per slide. `image_url` (desktop) is the required
// default; the rest fall back to it at render time. Keep the type_code-free
// keys in sync with ACE's heroSlideImageURL.
const HERO_IMAGE_SLOTS: { key: keyof HeroSlideMetadata; label: string; hint: string }[] = [
  { key: 'image_url', label: 'Desktop', hint: 'Default' },
  { key: 'image_url_tablet', label: 'Tablet', hint: 'Falls back to desktop' },
  { key: 'image_url_mobile', label: 'Mobile', hint: 'Falls back to desktop' },
  { key: 'image_url_tv', label: 'TV', hint: 'Falls back to desktop' },
]

/** A LinkRef as a hero slide stores it — the CMS's shared LinkRefValue minus
 * the form-only fields, with the inapplicable ids omitted rather than blank.
 * page_type is the field every shape carries; page_id addresses a custom page
 * and node_id a per-entity page's subject. */
export interface HeroSlideLinkRef {
  page_type: string
  page_id?: string
  node_id?: string
}

export interface HeroBannerMetadata {
  size?: 'LARGE' | 'STANDARD'
  rotation_interval_ms?: number
  slides?: HeroSlideMetadata[]
}

interface HeroBannerEditorProps {
  metadata: HeroBannerMetadata
  onChange: (metadata: HeroBannerMetadata) => void
  /** Present once the widget row exists (auto-created on name blur); required to upload slide images. */
  widgetId?: string
}

interface Opt {
  value: string
  label: string
}

// The dimensions are the desktop artwork each size is authored at, and they are
// the ratio the clients render the banner at — so art at the other size's shape
// gets cropped left and right, not letterboxed.
const SIZE_OPTIONS: Opt[] = [
  { value: 'LARGE', label: 'Large — 1920×475 desktop' },
  { value: 'STANDARD', label: 'Standard — 1920×333 desktop' },
]

const MODE_OPTIONS: Opt[] = [
  { value: 'image', label: 'Image' },
  { value: 'match', label: 'Match card (generated)' },
]

/** The generated desktop render for a match slide, as the row thumbnail. The
 * renders live on the match (tc_image_link, purpose = template key), so this
 * reads the match's images and picks the one this slide's template produced.
 * Empty until the pipeline's first successful render lands — same condition
 * under which ACE serves the slide at all. */
const MatchCardThumb: React.FC<{ matchId?: string; templateKey?: string }> = ({
  matchId,
  templateKey,
}) => {
  const { data } = useSWR(
    matchId ? ['match-images', matchId] : null,
    () => fetchMatchImages(matchId!),
  )
  const url = templateKey
    ? data?.images.find(
        (img) => img.type_code === 'hero_card_desktop' && img.purpose === templateKey,
      )?.cdn_url
    : undefined
  return url ? (
    <img src={url} alt="" className={styles.thumb} title="Latest generated card" />
  ) : (
    <span className={styles.thumbEmpty} title="No card generated yet" />
  )
}

const HeroBannerEditor: React.FC<HeroBannerEditorProps> = ({ metadata, onChange, widgetId }) => {
  const slides = metadata.slides ?? []
  const { siteId } = useWidgetSite()

  // The page-type vocabulary behind each slide's destination picker. Fetched
  // here too, on the key LinkRefSelector uses, so the completeness badge and
  // the collapsed row's summary can read a destination without waiting for a
  // slide to be expanded. SWR dedupes the two.
  const { data: typeConfig } = useSWR(
    siteId ? ['page-type-config', siteId] : null,
    () => fetchPageTypeConfig(siteId),
    { revalidateOnFocus: false },
  )
  const pageTypes = useMemo<PageTypeInfo[]>(() => typeConfig?.types ?? [], [typeConfig])

  // Hero artwork templates for the match-card dropdowns. The list endpoint
  // has no canvas info, so both dropdowns offer every hero template — the
  // desktop/mobile split lives in the template names. Unpublished templates
  // are flagged: a slide naming one renders nothing until it's published.
  // Focus revalidation stays ON (unlike the templates list page, which
  // refreshes itself after creates): the typical flow is "create a hero
  // template in another tab, come back here and pick it" — without the
  // refetch-on-focus the dropdown serves a pre-creation cache until reload.
  const { data: templatesResp } = useSWR('artwork-templates', fetchArtworkTemplates)
  const heroTemplateOptions = useMemo<Opt[]>(
    () =>
      (templatesResp?.templates ?? [])
        .filter((t) => t.template_type === 'hero' && t.is_active)
        .map((t) => ({
          value: t.key,
          label: `${t.name || t.key}${t.live_version ? '' : ' — unpublished'}`,
        })),
    [templatesResp],
  )

  // Latest metadata for async callbacks (image upload) that would otherwise
  // close over a stale render's slides/index and write to the wrong slide.
  const metadataRef = useRef(metadata)
  metadataRef.current = metadata

  // Which slides are expanded for editing. Collapsed rows are compact and
  // draggable; a newly added slide opens expanded so it can be filled in.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // The destination picker's value per slide, while it is being filled in. A
  // slide stores only a finished destination — a raw href, or a page reference
  // — so a half-made one has nowhere to live: choosing "External URL…" and not
  // yet typing the URL, or clearing the URL to retype it, would round-trip
  // through the slide as "nothing chosen" and snap the picker back to empty.
  const [linkDrafts, setLinkDrafts] = useState<Record<string, LinkRefValue>>({})

  const linkValueFor = (slide: HeroSlideMetadata): LinkRefValue =>
    linkDrafts[slide.id] ?? slideLinkRefValue(slide)

  const setSlideLink = (id: string, v: LinkRefValue) => {
    setLinkDrafts(prev => ({ ...prev, [id]: v }))
    patchSlideById(id, slideLinkPatch(v))
  }

  // Dropping the draft is what makes the slide the source of truth again —
  // used when the slide's destination changes out from under the picker.
  const clearLinkDraft = useCallback((id: string) => {
    setLinkDrafts(prev => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  const update = useCallback(
    (patch: Partial<HeroBannerMetadata>) => onChange({ ...metadataRef.current, ...patch }),
    [onChange],
  )

  const commitSlides = useCallback(
    (next: HeroSlideMetadata[]) => update({ slides: next }),
    [update],
  )

  // Patch a slide by id off the latest metadata. Use this from async callbacks
  // (upload) so a reorder/edit in flight can't misroute the result.
  const patchSlideById = useCallback(
    (id: string, patch: Partial<HeroSlideMetadata>) => {
      const cur = metadataRef.current.slides ?? []
      commitSlides(cur.map(s => (s.id === id ? { ...s, ...patch } : s)))
    },
    [commitSlides],
  )

  // Switching mode clears the other mode's fields so a slide is never both:
  // an image alongside a match ref would make ACE serve the stale image while
  // the pipeline renders cards nobody displays.
  const setSlideMode = useCallback(
    (id: string, mode: 'image' | 'match') => {
      // Match mode has no authored destination and image mode starts without
      // one, so neither keeps the picker's in-progress value.
      clearLinkDraft(id)
      if (mode === 'match') {
        patchSlideById(id, {
          image_url: '',
          image_url_tablet: undefined,
          image_url_mobile: undefined,
          image_url_tv: undefined,
          link: '',
          link_ref: undefined,
          content_type: 'match',
        })
      } else {
        patchSlideById(id, {
          content_type: undefined,
          content_id: undefined,
          hero_template_desktop: undefined,
          hero_template_mobile: undefined,
        })
      }
    },
    [patchSlideById, clearLinkDraft],
  )

  const { dragIndex, handleProps, rowProps } = useDragReorder(
    useCallback(
      (from: number, to: number) => commitSlides(arrayMove(metadataRef.current.slides ?? [], from, to)),
      [commitSlides],
    ),
    { dragImageFromParent: true },
  )

  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const addSlide = () => {
    const id = crypto.randomUUID()
    commitSlides([...slides, { id, image_url: '', link: '' }])
    setExpanded(prev => new Set(prev).add(id))
  }

  const removeSlide = (id: string) => {
    commitSlides((metadataRef.current.slides ?? []).filter(s => s.id !== id))
    setExpanded(prev => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const sizeValue = SIZE_OPTIONS.find(o => o.value === (metadata.size ?? 'LARGE')) ?? SIZE_OPTIONS[0]

  return (
    <>
      <div className={formStyles.formGrid}>
        <div className={formStyles.fieldGroup}>
          <label title="The banner's height, and the shape of the desktop artwork it expects: Large is 1920×475, Standard is 1920×333.">Size</label>
          <Select
            value={sizeValue}
            onChange={(opt) => opt && update({ size: opt.value as HeroBannerMetadata['size'] })}
            options={SIZE_OPTIONS}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
          />
        </div>

        <div className={formStyles.fieldGroup}>
          <label title="How long each slide shows before rotating to the next.">Rotation interval (ms)</label>
          <input
            type="number"
            min={0}
            placeholder="e.g. 6000 — leave blank to not auto-rotate"
            value={metadata.rotation_interval_ms ?? ''}
            onChange={(e) =>
              update({ rotation_interval_ms: e.target.value ? parseInt(e.target.value, 10) : undefined })
            }
          />
        </div>
      </div>

      <div className={styles.slidesHeader}>
        <span>Slides</span>
        <Badge variant="secondary" size="small">{slides.length}</Badge>
      </div>

      {!widgetId && (
        <Banner variant="info" message="Name the widget above to unlock slide image uploads." />
      )}

      {slides.length === 0 && (
        <p className={formStyles.hint}>No slides yet — a hero banner needs at least one.</p>
      )}

      {slides.map((slide, i) => {
        const isOpen = expanded.has(slide.id)
        const matchMode = isMatchSlide(slide)
        // Mirrors the API's hero validation: an image slide needs an image and
        // a link; a match-card slide needs a match and both template picks.
        const missing = matchMode
          ? !slide.content_id
            ? 'match'
            : !slide.hero_template_desktop || !slide.hero_template_mobile
              ? 'templates'
              : null
          : !slide.image_url
            ? 'image'
            : validateLinkRef(linkValueFor(slide), pageTypes)
              ? 'link'
              : null
        const linkSummary = slideLinkSummary(slide, pageTypes)
        return (
          <div
            key={slide.id}
            className={`${styles.slide} ${dragIndex === i ? styles.dragging : ''}`}
            {...rowProps(i)}
          >
            <div className={styles.slideRow}>
              <span
                className={styles.handle}
                title="Drag to reorder"
                aria-label="Drag to reorder"
                {...handleProps(i)}
              >
                ⠿
              </span>

              {matchMode ? (
                <MatchCardThumb
                  matchId={slide.content_id}
                  templateKey={slide.hero_template_desktop}
                />
              ) : slide.image_url ? (
                <img src={slide.image_url} alt="" className={styles.thumb} />
              ) : (
                <span className={styles.thumbEmpty} />
              )}

              <button type="button" className={styles.summary} onClick={() => toggleExpanded(slide.id)}>
                <span className={styles.summaryTitle}>{`Slide ${i + 1}`}</span>
                {matchMode ? (
                  <span className={styles.summaryLink}>Match card (generated)</span>
                ) : (
                  linkSummary && <span className={styles.summaryLink}>{linkSummary}</span>
                )}
              </button>

              {missing && (
                <Badge variant="warning" size="small">Needs {missing}</Badge>
              )}

              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => toggleExpanded(slide.id)}
                title={isOpen ? 'Collapse' : 'Edit'}
                aria-expanded={isOpen}
              >
                {isOpen ? '▲' : '▼'}
              </button>
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.remove}`}
                onClick={() => removeSlide(slide.id)}
                title="Remove slide"
              >
                ✕
              </button>
            </div>

            {isOpen && (
              <div className={styles.slideBody}>
                <div className={formStyles.fieldGroup}>
                  <label title="Image slides use uploaded artwork; match-card slides are generated from a match and hero templates, and regenerate as the match's state changes.">
                    Slide type
                  </label>
                  <Select
                    value={MODE_OPTIONS[matchMode ? 1 : 0]}
                    onChange={(opt) =>
                      opt && setSlideMode(slide.id, opt.value as 'image' | 'match')
                    }
                    options={MODE_OPTIONS}
                    className={selectStyles.reactSelect}
                    classNamePrefix="react-select"
                  />
                </div>

                {matchMode ? (
                  <>
                    <div className={formStyles.fieldGroup}>
                      <label>Match</label>
                      <MatchSelect
                        value={slide.content_id ?? ''}
                        onChange={(id) =>
                          patchSlideById(slide.id, { content_id: id || undefined })
                        }
                      />
                      <small className={formStyles.hint}>{MATCH_SEARCH_HINT}</small>
                    </div>

                    <div className={formStyles.fieldGroup}>
                      <label>Desktop template</label>
                      <Select
                        value={
                          heroTemplateOptions.find(
                            (o) => o.value === slide.hero_template_desktop,
                          ) ??
                          (slide.hero_template_desktop
                            ? { value: slide.hero_template_desktop, label: slide.hero_template_desktop }
                            : null)
                        }
                        onChange={(opt) =>
                          patchSlideById(slide.id, {
                            hero_template_desktop: opt?.value || undefined,
                          })
                        }
                        options={heroTemplateOptions}
                        isClearable
                        placeholder="Pick a 1920×475 hero template…"
                        noOptionsMessage={() => 'No hero templates yet — create one under Artwork Templates'}
                        className={selectStyles.reactSelect}
                        classNamePrefix="react-select"
                      />
                    </div>

                    <div className={formStyles.fieldGroup}>
                      <label>Mobile template</label>
                      <Select
                        value={
                          heroTemplateOptions.find(
                            (o) => o.value === slide.hero_template_mobile,
                          ) ??
                          (slide.hero_template_mobile
                            ? { value: slide.hero_template_mobile, label: slide.hero_template_mobile }
                            : null)
                        }
                        onChange={(opt) =>
                          patchSlideById(slide.id, {
                            hero_template_mobile: opt?.value || undefined,
                          })
                        }
                        options={heroTemplateOptions}
                        isClearable
                        placeholder="Pick a 4:3 hero template…"
                        noOptionsMessage={() => 'No hero templates yet — create one under Artwork Templates'}
                        className={selectStyles.reactSelect}
                        classNamePrefix="react-select"
                      />
                    </div>

                    <p className={formStyles.hint}>
                      The card image is generated automatically and refreshes as
                      the match moves through upcoming, live, and replay. The
                      click-through follows the match, so there’s no link to
                      set.
                    </p>
                  </>
                ) : (
                  <div className={styles.imageSlots}>
                    {HERO_IMAGE_SLOTS.map((sl) => {
                      const url = (slide[sl.key] as string | undefined) || ''
                      return (
                        <ImageSlot
                          key={sl.key}
                          label={sl.label}
                          hint={sl.hint}
                          aspectRatio="16 / 9"
                          disabled={!widgetId}
                          image={url ? { url } : null}
                          onUpload={async (file) => {
                            const { cdn_url } = await uploadHeroImage(widgetId!, file, slide.alt_text)
                            patchSlideById(slide.id, { [sl.key]: cdn_url } as Partial<HeroSlideMetadata>)
                          }}
                          onRemove={url ? () => patchSlideById(slide.id, { [sl.key]: '' } as Partial<HeroSlideMetadata>) : undefined}
                        />
                      )
                    })}
                  </div>
                )}

                <div className={formStyles.fieldGroup}>
                  <label>Alt text <Badge variant="secondary" size="small">Public</Badge></label>
                  <input
                    type="text"
                    value={slide.alt_text ?? ''}
                    onChange={(e) => patchSlideById(slide.id, { alt_text: e.target.value || undefined })}
                    placeholder="Describe the image for screen readers"
                  />
                </div>

                {!matchMode && (
                  <LinkRefSelector
                    siteId={siteId}
                    value={linkValueFor(slide)}
                    onChange={(v) => setSlideLink(slide.id, v)}
                    allowExternal
                  />
                )}
              </div>
            )}
          </div>
        )
      })}

      <button type="button" onClick={addSlide} className={styles.addSlide}>
        + Add slide
      </button>
    </>
  )
}

export default HeroBannerEditor
